/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { isElectron } from "react-device-detect";

import { useCurrentUser } from "src/app/useCurrentUser";
import { Block } from "src/classes/block";
import { BlockWindow, ImageBlock } from "src/classes/block/block-types";
import { Rectangle } from "src/classes/plate/lib/types";
import { ImageMenuItems } from "src/components/Block/ContextMenu/ImageMenuItems";
import { ConnectMenu } from "src/components/Shared/ConnectMenu";
import { Menu } from "src/components/UI/Menus/Menu";
import { MenuBreak } from "src/components/UI/Menus/MenuItem/MenuBreak";
import { MenuItem } from "src/components/UI/Menus/MenuItem/MenuItem";

interface Props {
  block: Block;
  editable: boolean;
  anchor: Rectangle;
  close: () => void;
}
export function BlockContextMenu({ block, editable, anchor, close }: Props) {
  const loggedIn = !!useCurrentUser();
  const isLiked = block.observe((state) => state.isLiked);

  // if block window, render context menu for embedded block
  return block instanceof BlockWindow && block.embeddedBlock ? (
    <BlockContextMenu block={block.embeddedBlock} editable={editable} anchor={anchor} close={close} />
  ) : (
    <Menu anchor={anchor} close={close} blockMouseEvents={true}>
      <MenuItem handleClick={() => block.open()}>Open</MenuItem>
      <MenuItem condition={!isElectron} handleClick={() => block.openInNewTab()}>
        Open in New Tab
      </MenuItem>
      <MenuItem handleClick={() => block.copyLink()}>Copy link</MenuItem>
      {block instanceof ImageBlock && (
        <>
          <MenuItem handleClick={() => block.downloadImage()}>Download Image</MenuItem>
          <MenuItem handleClick={() => block.copyImage()}>Copy image</MenuItem>
        </>
      )}
      <MenuBreak condition={loggedIn} />
      <MenuItem condition={loggedIn} handleClick={() => block.like()}>
        {isLiked ? "Unlike" : "Like"}
      </MenuItem>
      <MenuItem
        condition={loggedIn}
        hasSubOptions
        hoverComponent={<ConnectMenu block={block} plate={null} blockMouseEvents={false} />}
      >
        Add To Plate
      </MenuItem>
      {block instanceof ImageBlock && editable && <ImageMenuItems block={block} />}
    </Menu>
  );
}
